import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { z } from 'zod';

type RawHandler = (data: unknown) => void;

/**
 * Flux d'événements serveur (`GET /api/stream`, CDC §3.7) : une seule connexion EventSource pour
 * toute l'appli, ouverte par la coquille quand on est connecté. EventSource se reconnecte seul.
 */
export const useStreamStore = defineStore('stream', () => {
  const connected = ref(false);
  let source: EventSource | null = null;
  const handlers = new Map<string, Set<RawHandler>>();
  const attached = new Set<string>();

  function dispatch(type: string, raw: string): void {
    let data: unknown;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    for (const handler of handlers.get(type) ?? []) handler(data);
  }

  function attach(type: string): void {
    if (!source || attached.has(type)) return;
    attached.add(type);
    source.addEventListener(type, (event) => dispatch(type, (event as MessageEvent<string>).data));
  }

  function start(): void {
    if (source || typeof EventSource === 'undefined') return;
    source = new EventSource('/api/stream', { withCredentials: true });
    source.onopen = () => {
      connected.value = true;
    };
    source.onerror = () => {
      connected.value = false;
    };
    for (const type of handlers.keys()) attach(type);
  }

  function stop(): void {
    source?.close();
    source = null;
    attached.clear();
    connected.value = false;
  }

  /** Écoute un type d'événement ; la donnée est validée par le schéma partagé. Renvoie le désabonnement. */
  function on<S extends z.ZodType>(
    type: string,
    schema: S,
    handler: (data: z.output<S>) => void,
  ): () => void {
    const wrapped: RawHandler = (data) => {
      const parsed = schema.safeParse(data);
      if (parsed.success) handler(parsed.data);
    };
    const set = handlers.get(type) ?? new Set<RawHandler>();
    set.add(wrapped);
    handlers.set(type, set);
    attach(type);
    return () => {
      set.delete(wrapped);
    };
  }

  return { connected, start, stop, on };
});
